import { useCallback, useEffect, useState } from "react";
import { Megaphone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ChatThread, ThreadList } from "@/components/Chat";
import { PushToggle } from "@/components/PushToggle";
import { PageHeader } from "@/pages/admin/AdminLayout";

function BroadcastDialog({ open, onOpenChange, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    if (!open) { return; }
    api.get("/admin/technicians").then((r) => {
      const act = r.data.filter((u) => u.active !== false);
      setUsers(act); setSel([]);
    });
  }, [open]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/chat/messages", { text, broadcast: true, recipient_ids: sel });
      toast.success(`Inviato a ${data.sent} utenti`);
      setText("");
      onOpenChange(false);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const groups = [["Tutti", users], ["Solo tecnici", users.filter((u) => u.role === "tecnico")], ["Solo responsabili", users.filter((u) => u.role === "responsabile")],
    ["Solo dipendenti", users.filter((u) => u.employment !== "autonomo")]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="broadcast-dialog">
        <DialogHeader><DialogTitle className="font-display">Messaggio a un gruppo</DialogTitle>
          <DialogDescription>Scegli i destinatari: arriva nella chat di ognuno, con notifica.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {groups.map(([l, g], i) => (
            <Button key={l} size="sm" variant="outline" className="rounded-full" onClick={() => setSel(g.map((u) => u.id))} data-testid={`broadcast-group-${i}`}>{l}</Button>
          ))}
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setSel([])} data-testid="broadcast-group-none">Nessuno</Button>
        </div>
        <div className="max-h-48 divide-y overflow-y-auto rounded-xl border" data-testid="broadcast-recipients">
          {users.map((u, i) => (
            <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`broadcast-recipient-${i}`} />
              <span className="font-medium">{u.name}</span>
              <span className="text-xs text-slate-500">{u.role === "responsabile" ? "Responsabile" : u.employment === "autonomo" ? "Tecnico autonomo" : "Tecnico"}</span>
            </label>
          ))}
        </div>
        <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi il messaggio…" data-testid="broadcast-input" />
        <DialogFooter><Button onClick={send} disabled={busy || !text.trim() || !sel.length} className="rounded-full" data-testid="broadcast-send-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Invia a {sel.length}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/chat/threads").then((r) => setThreads(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const key = sel ? `${sel.technician_id}:${sel.job_id || ""}` : null;

  return (
    <div>
      <PageHeader eyebrow="Comunicazioni" title="Messaggi">
        <div className="flex flex-wrap items-center gap-3">
          <PushToggle />
          <Button variant="outline" onClick={() => setOpen(true)} className="rounded-full" data-testid="broadcast-button"><Megaphone className="h-4 w-4" /> Messaggio a un gruppo</Button>
        </div>
      </PageHeader>
      <div className="grid gap-5 lg:grid-cols-12">
        <div className="max-h-[560px] overflow-y-auto rounded-2xl border bg-white lg:col-span-4">
          <ThreadList threads={threads} selected={key} onSelect={setSel} showTech />
        </div>
        <div className="rounded-2xl border bg-white p-4 lg:col-span-8">
          {sel ? (
            <>
              <p className="mb-3 font-display font-semibold" data-testid="chat-thread-title">{sel.technician_name}{sel.job_label && <span className="font-normal text-slate-500"> · {sel.job_label}</span>}</p>
              <ChatThread technicianId={sel.technician_id} jobId={sel.job_id} className="h-[480px]" onSent={load} />
            </>
          ) : <p className="py-20 text-center text-sm text-slate-500" data-testid="chat-no-selection">Seleziona una conversazione.</p>}
        </div>
      </div>
      <BroadcastDialog open={open} onOpenChange={setOpen} onSent={load} />
    </div>
  );
}
